import { organizationApi } from '@/api/account-management/organizationApi';
import { type Organization } from '@/models/account-management/Organization';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import Spacer from '../common/Spacer';
import { isStytchError } from './common';
import { buildPasswordFeedbackMessages } from './passwordFeedback';

type InviteForm = {
    email: string;
    password: string;
    confirmPassword: string;
};

const Invite = (): ReactElement => {
    const stytch = useStytchB2BClient();
    const navigate = useNavigate();
    const strings = useLocalizedStrings();
    const qc = useQueryClient();
    const { register, handleSubmit: validateAndSubmit, getValues, setError, clearErrors, formState } = useForm<InviteForm>({
        defaultValues: { email: '', password: '', confirmPassword: '' },
    });

    const handleInvite = async ({ email, password }: InviteForm) => {
        clearErrors('root');
        try {
            const token = new URLSearchParams(window.location.search).get("token");

            if (!token) {
                setError('root', { type: 'server', message: "Missing password reset token" });
                return;
            }

            const strengthResponse = await stytch.passwords.strengthCheck({ email_address: email, password: password });

            if (!strengthResponse.valid_password) {
                setError('root', { type: 'validation', message: buildPasswordFeedbackMessages(strengthResponse, strings).join("\n") });
                return;
            }

            const resetResponse = await stytch.passwords.resetByEmail({
                password_reset_token: token,
                password: password,
                session_duration_minutes: parseInt(import.meta.env.VITE_STYTCH_SESSION_DURATION)
            });

            const organizationId = resetResponse.organization?.organization_id;
            if (!organizationId) {
                navigate("/");
                return;
            }

            // Use the org id returned from Stytch to fetch the organization details and get the slug
            const organization = await qc.fetchQuery<Organization>({
                queryKey: ['organization', organizationId],
                queryFn: () => organizationApi.get(organizationId),
                staleTime: Infinity,
            });

            if (!organization.slug) {
                navigate("/");
                return;
            }

            // We can redirect to the log in screen with the organization slug (so we can skip the "choose organization" screen)
            navigate(`/${organization.slug}`);
        }
        catch (err) {
            console.error('Error:', err);
            if (isStytchError(err)) {
                setError('root', { type: 'server', message: err.error_message });
            } else {
                setError('root', { type: 'server', message: 'An unexpected error occurred' });
            }
        }
    };

    return (
        <Box
            sx={{
                '& > :not(style)': { m: 1, width: '40ch' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100dvh',
            }}
        >
            <Stack component="form" direction="column" onSubmit={validateAndSubmit(handleInvite)} noValidate>
                <Typography variant="h3" sx={{ alignSelf: 'center' }}>{strings.completeSignup}</Typography>
                <Spacer y={2} />
                <TextField
                    id="email"
                    type="email"
                    autoComplete="username"
                    label={strings.email}
                    variant="outlined"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                    error={!!formState.errors.email}
                    helperText={formState.errors.email?.message}
                    {...register('email', { required: strings.validationEmailRequired })} />
                <Spacer y={2} />
                <TextField
                    id="password"
                    label={strings.password}
                    type="password"
                    autoComplete="new-password"
                    variant="outlined"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                    error={!!formState.errors.password}
                    helperText={formState.errors.password?.message}
                    {...register('password', { required: strings.validationPasswordRequired })} />
                <Spacer y={2} />
                <TextField
                    id="confirm-password"
                    label={strings.confirmPassword}
                    type="password"
                    autoComplete="new-password"
                    variant="outlined"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                    error={!!formState.errors.confirmPassword}
                    helperText={formState.errors.confirmPassword?.message}
                    {...register('confirmPassword', {
                        required: strings.validationPasswordRequired,
                        validate: (value) => value === getValues('password') || strings.valitationPasswordMatch,
                    })}
                />
                <Spacer y={2} />
                <Button type="submit" variant="contained" disabled={formState.isSubmitting}>{strings.complete}</Button>
                <Spacer y={2} />
                <Typography sx={{ alignSelf: 'center', whiteSpace: 'pre-line' }} color="error">{formState.errors.root?.message}</Typography>
            </Stack>
        </Box>
    );
};

export default Invite;
